<template>
  <div class="media-player">
    <p class="play-input">
      <input type="radio"
             v-bind:name="`futuri-player-is-playing-${index}`"
             v-bind:id="`futuri-player-is-playing-${index}-1`"
             v-bind:checked="isPlaying"
             value="play"
             v-on:click="play">
      <label class="play-button" v-bind:for="`futuri-player-is-playing-${index}-1`">Play</label>

      <input type="radio"
             v-bind:name="`futuri-player-is-playing-${index}`"
             v-bind:id="`futuri-player-is-playing-${index}-2`"
             v-bind:checked="!isPlaying"
             value="pause"
             v-on:click="pause">
      <label class="pause-button" v-bind:for="`futuri-player-is-playing-${index}-2`">Pause</label>
    </p>
    <scrubber v-bind:position="seekPosition"
              v-bind:playing-live-stream="playingLiveStream"
              v-on:seeking="onSeeking">
    </scrubber>
    <p class="time-remaining-input">
      <span v-if="!playingLiveStream">-{{ formattedTimeLeft }}</span>
      <span v-else>LIVE</span>
    </p>
  </div>
</template>

<script>
import Scrubber from './scrubber';

export default {
  components: { Scrubber },
  // Vue3: declare emitted events so the `@play`/`@pause`/`@seeking` listeners on
  // <media-player> bind as component events, not native-attr fallthrough.
  emits: ['play', 'pause', 'seeking'],
  props: ['station', 'id', 'index', 'duration', 'dropToLive', 'isPlaying', 'seekPosition', 'timeLeft', 'playingLiveStream'],
  computed: {
    formattedTimeLeft() {
      let sec = this.timeLeft;

      const hours = Math.floor(sec / 3600.0);
      sec -= hours * 3600.0;
      const minutes = Math.floor(sec / 60.0);
      sec -= minutes * 60.0;

      const zeroPad = (value) => {
        const str = Math.floor(value).toString();
        if (str.length < 2) {
          return `0${str}`;
        }
        return str;
      };
      const text = `${zeroPad(minutes)}:${zeroPad(sec)}`;
      if (hours > 0) {
        return `${Math.floor(hours).toString()}:${text}`;
      }
      return text;
    },
  },
  methods: {
    play() {
      this.$emit('play');
    },

    pause() {
      this.$emit('pause');
    },

    onSeeking(pos) {
      this.$emit('seeking', pos);
    },
  },
};
</script>

<style lang="less" scoped>
  .faded-background(@color) { background: @color; background: linear-gradient(@color, fade(@color, 50%)); }

  * {box-sizing: border-box; margin: 0; padding: 0;}

  .media-player {
    display: none;
    position: absolute;
    bottom: 0;
    left:0;
    width: 100%;
    border-radius: 0 0 10px 10px;
    overflow: hidden;
    padding: 0;
    margin: 0;
    font-size: 0;

    p {
      display: inline-block;
      vertical-align: top;
      line-height: 42px;
      height: 42px;
      .faded-background(black);
      color: white;
      background-clip: padding-box;
      border-right: solid 1px rgba(255, 255, 255, .3);
      padding: 0;
      margin: 0;

      &.play-input {
        width: 42px;

        input[type=radio] {
          display: none;
        }

        label {
          font-size: 0;
          display: block;
          width: 100%;
          height: 100%;
          text-align: center;
          cursor: pointer;

          &.pause-button {
            &:after {
              content: "";
              background-image: url(../assets/pause.svg);
              background-size: cover;
              width: 16px;
              height: 16px;
              margin-top: 13px;
              display: inline-block;
            }
          }
          &.play-button {
            &:after {
              content: "";
              background-image: url(../assets/play.svg);
              background-size: cover;
              width: 16px;
              height: 16px;
              margin-top: 13px;
              display: inline-block;
            }
          }
        }

        input[type=radio]:checked + label {
          display: none;
        }

        // R11 (a11y): the play/pause label renders 41x42 on a phone, just under
        // the plan's 44px touch-target figure (§7.4). Expand the HIT AREA only
        // (transparent, out of flow, pixel-identical). HEIGHT is deliberately
        // 42, not 44: `.media-player` is `overflow: hidden` to keep the bar's
        // rounded bottom corners, so anything taller than the 42px bar is
        // clipped — and a clipped region is not hit-testable either. 42px still
        // clears WCAG 2.5.8 AA (24px), and the artwork label above the bar is a
        // second, much larger play target for the same episode.
        // @media(max-width)-gated ONLY.
        @media (max-width: 480px) {
          label {
            position: relative;

            // Anchored LEFT, not centred: the play cell is flush to the frame's
            // left edge, so a centred box would hang outside the document where
            // it cannot be tapped.
            &:before {
              content: "";
              position: absolute;
              top: 50%;
              left: 0;
              transform: translateY(-50%);
              width: 44px;
              height: 42px;
            }
          }
        }

        &:hover {
          .faded-background(lighten(black, 40%));
        }
      }

      &.scrub-input {
        width: ~"calc(100% - 42px - 72px)"; // - 42px
        padding: 3px 16px;
        input {
          width: 100%;
          height: 100%;
          cursor: pointer;
        }

        // The transport row's min-content floor was 147px: 42 (play) + 33 (this
        // cell's 16px padding either side + the input's intrinsic minimum) + 72
        // (time). Below ~200px the scrub track degenerated to 6-21px, which is
        // not draggable.
        //
        // Trimming the horizontal padding is what actually lowers the floor —
        // `min-width: 0` alone does not, because the padding IS the floor.
        // Measured after this change with a min-content probe (clamp the iframe
        // document to 1px and read what refuses to shrink): 42 + 13 + 52 = 107.
        // Pinned by responsive.spec.js "transport row min-content stays under
        // the narrowest column we support" (asserts <= 120, so reverting either
        // half of this trim reds it).
        // @media(max-width)-gated ONLY, so the desktop row is untouched.
        //
        // ⚠️ This comment previously claimed the trim "did NOT move the overflow
        // floor", on the strength of a re-measurement that still showed
        // column=128 -> iframe=153. That was a misattribution: 153 was never
        // min-content pressure, it was the embedder applying a TRANSIENT posted
        // width verbatim and never re-measuring. `classes/widget.js` now clamps
        // the posted width, and both halves are separately pinned. See the R2
        // block in responsive.spec.js for the full history.
        @media (max-width: 480px) {
          min-width: 0;
          padding-left: 6px;
          padding-right: 6px;
          // track the narrowed time cell below (72 -> 52)
          width: ~"calc(100% - 42px - 52px)";
        }
      }

      &.time-remaining-input {
        width: 72px;
        border-right: 0;

        // Second half of the min-content-floor fix above. 72px is sized for
        // "-00:00:00"; below 480px the durations that actually render are
        // "-00:00" or shorter, so the cell can give back 20px without clipping.
        // @media(max-width)-gated ONLY.
        @media (max-width: 480px) {
          width: 52px;
        }

        span {
          font-family: "Helvetica Neue",Helvetica,Arial,sans-serif;
          font-weight: normal;
          font-size: 12px;
          display: block;
          width: 100%;
          height: 100%;
          text-align: center;
        }
      }
    }
  }
</style>
